import { mapMemberError } from "@/api/members/mapMemberError";
import {
  toDirectoryMember,
  type MemberStatus,
  type OrgDirectoryMember,
  type OrgMember,
} from "@/api/members/memberList";
import { supabase } from "@/api/supabaseClient";
import { isOrgMembershipRole, type OrgMembershipRole } from "@/auth/membershipRoles";
import type { Database } from "@/types/database";

type MembershipRole = Database["public"]["Enums"]["membership_role"];
type MembershipStatus = Database["public"]["Enums"]["membership_status"];

function fail(scope: string, error: unknown): never {
  console.error(scope, error);
  throw new Error(mapMemberError(error));
}

function asStatus(value: string): MemberStatus {
  if (value === "invited" || value === "disabled" || value === "active") return value;
  return "active";
}

export class MembersApi {
  static async listOrgMembers(orgId: string): Promise<OrgMember[]> {
    const { data, error } = await supabase.rpc("list_org_members", { p_org: orgId });
    if (error) fail("list_org_members", error);
    return (data ?? []).map((row) => ({
      membershipId: row.membership_id,
      profileId: row.profile_id,
      displayName: row.display_name,
      email: row.email,
      status: asStatus(row.status),
      roles: row.roles ?? [],
      invitedAt: row.invited_at,
      activatedAt: row.activated_at,
      disabledAt: row.disabled_at,
      createdAt: row.created_at,
    }));
  }

  static async listOrgMemberDirectory(orgId: string): Promise<OrgDirectoryMember[]> {
    const { data, error } = await supabase.rpc("list_org_member_directory", {
      p_org: orgId,
    });
    if (error) fail("list_org_member_directory", error);
    return (data ?? []).map((row) => toDirectoryMember(row));
  }

  static async setMemberRoles(
    membershipId: string,
    roles: readonly OrgMembershipRole[],
  ): Promise<string[]> {
    const payload = roles.filter(isOrgMembershipRole) as MembershipRole[];
    const { data, error } = await supabase.rpc("set_member_roles", {
      p_membership: membershipId,
      p_roles: payload,
    });
    if (error) fail("set_member_roles", error);
    return data ?? [];
  }

  static async setMemberStatus(
    membershipId: string,
    status: "active" | "disabled",
  ): Promise<MembershipStatus> {
    const { data, error } = await supabase.rpc("set_member_status", {
      p_membership: membershipId,
      p_status: status,
    });
    if (error) fail("set_member_status", error);
    return data as MembershipStatus;
  }
}
